<template>
    <div
        v-on-clickaway="closeDropdown"
        class="navbar-form navbar-right"
        @click.capture="handleClick"
        @keyup.capture.exact.up="handleUp"
        @keyup.capture.exact.down="handleDown"
        @keyup.capture.exact.esc="handleEscape"
    >

        <div class="dropdown">

            <form
                ref="form"
                class="navbar-form navbar-right m-0"
                :action="searchUrl"
                method="GET"
            >
                <div class="form-group">
                    <div
                        id="navbar-search"
                        class="input-group input-group-sm"
                    >
                        <input
                            ref="input"
                            v-model="query"
                            type="search"
                            placeholder="Search for something..."
                            name="search-query"
                            class="form-control"
                            autocomplete="off"
                            @input="debouncedSearch"
                        >
                        <div class="input-group-append">
                            <button
                                tabindex="-1"
                                class="btn btn-primary"
                                type="submit"
                            >
                                <i class="fa fa-search" />
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            <div
                class="dropdown-menu dropdown-menu-right"
                :class="{ 'show' : isDropdownOpen }"
            >
                <div
                    v-for="(typeResults, type) in results"
                    :key="type"
                >
                    <h6 class="dropdown-header">
                        {{ type }}
                    </h6>
                    <div
                        v-for="(result, index) in typeResults"
                        :key="index"
                    >
                        <a
                            :href="result.link"
                            class="dropdown-item"
                        >
                            {{ result.name }}
                        </a>
                    </div>
                    {{ results[results.length - 1] }}
                    <div
                        v-if="type !== Object.keys(results)[Object.keys(results).length-1]"
                        class="dropdown-divider"
                    />
                </div>
                <a
                    href="#"
                    class="dropdown-item"
                    @click="$refs.form.submit()"
                >
                    View all results...
                </a>
            </div>

        </div>

    </div>
</template>

<script>
import axios from 'axios';
import queryString from 'query-string';
import { debounce } from 'throttle-debounce';
import { mixin as clickaway } from 'vue-clickaway';

export default {

    mixins: [
        clickaway,
    ],

    props: {
        searchUrl: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            query: '',
            results: [],
            hasResults: false,
            isDropdownOpen: false,
            focusedItemIndex: null,
        };
    },

    created() {
        this.populateInput();
        this.addShortkeyListener();
    },

    beforeDestroy() {
        this.removeShortkeyListener();
    },

    methods: {
        populateInput() {
            const queryParams = queryString.parse(window.location.search);
            const searchQuery = queryParams['search-query'];
            if (searchQuery) {
                this.query = searchQuery;
            }
        },
        async search(value) {
            const encodedValue = encodeURIComponent(value);

            const response = await axios.get(`/api/app-v1/search/suggestions?query=${encodedValue}`);
            if (response.status && response.status === 200) {
                const results = response.data;
                this.results = results
                    .filter(Boolean)
                    .reduce((acc, result) => {
                        acc[result.type] = [
                            ...acc[result.type] || [],
                            result,
                        ];
                        return acc;
                    }, {});
                this.hasResults = results.length > 0;
                this.openDropdownIfResults();
            } else {
                // fail
            }
        },
        debouncedSearch: debounce(250, function _() {
            if (this.query) {
                this.search(this.query);
            } else {
                this.closeDropdown();
                this.hasResults = false;
            }
        }),
        openDropdownIfResults() {
            this.isDropdownOpen = this.hasResults;
        },
        closeDropdown() {
            this.isDropdownOpen = false;
            this.focusedItemIndex = null;
        },
        handleClick() {
            this.openDropdownIfResults();
            if (this.query && !this.hasResults) {
                this.search(this.query);
            }
        },
        addShortkeyListener() {
            window.addEventListener('keydown', this.shortkeyHandler);
        },
        removeShortkeyListener() {
            window.removeEventListener('keydown', this.shortkeyHandler);
        },
        shortkeyHandler(e) {
            const isModifierActive = () => e.getModifierState('Control')
                || e.getModifierState('Meta')
                || e.getModifierState('OS')
                || e.getModifierState('Win');

            if (e.keyCode === 74 && isModifierActive()) {
                e.preventDefault();
                e.stopPropagation();
                this.handleShortkey();
            }
        },
        handleShortkey() {
            this.$refs.input.focus();
            this.openDropdownIfResults();
            if (this.query && !this.hasResults) {
                this.search(this.query);
            }
        },
        handleEscape() {
            this.closeDropdown();
            this.$refs.input.focus();
        },
        handleUp() {
            if (!this.isDropdownOpen) return;
            const items = this.$el.querySelectorAll('.dropdown-item');
            const index = this.focusedItemIndex ? this.focusedItemIndex - 1 : items.length - 1;
            items[index].focus();
            this.focusedItemIndex = index;
        },
        handleDown() {
            if (!this.isDropdownOpen) {
                this.openDropdownIfResults();
                return;
            }
            const items = this.$el.querySelectorAll('.dropdown-item');
            const index = this.focusedItemIndex !== null && this.focusedItemIndex < items.length - 1 ? this.focusedItemIndex + 1 : 0;
            items[index].focus();
            this.focusedItemIndex = index;
        },
    },

};
</script>

<style lang="scss" scoped>
.dropdown-menu {
    min-width: 100%;
}

.form-control {
    width: 300px;
}
</style>
